import { Alert, Badge, Divider, Group, Paper, Stack, Text, TextInput } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import ConfirmationModal from '@/components/ConfirmationModal.tsx';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import StyledSelect from '@/components/StyledInputFields/StyledSelect.tsx';
import SingleDataInputSelectorInner from '@/pages/Editor/components/SharedFields/DataInputSelector/SingleDataInputSelectorInner.tsx';
import useCompositionOperations, {
  CompositionOperation,
} from '@/hooks/api/automations/useCompositionOperations.ts';
import useRetrieveEditorCompositionCapabilities from '@/hooks/api/automations/useRetrieveEditorCompositionCapabilities.ts';
import useRetrieveLatestAutomationVersion from '@/hooks/api/automationVersions/useRetrieveLatestAutomationVersion.ts';
import { useActiveCompositionTarget } from '@/layouts/AutomationPageWrapper/hooks/useActiveCompositionTarget.ts';
import { DataInput, DatasetType } from '@/types/automationFields.ts';
import { AutomationStep, LocalSubflowCallStep } from '@/types/automationSteps.ts';
import {
  AutomationLocalSubflow,
  AutomationLocalSubflowOutputPort,
  AutomationLocalSubflowPort,
} from '@/types/automations.ts';
import {
  AttachCall,
  PortList,
} from './LocalSubflowInterfaceControls.tsx';
import { flattenLocalSubflowSteps } from './localSubflowStepUtils.ts';
import {
  assignedLocalSubflowPortId,
  callersFor,
  DEFAULT_LOCAL_SUBFLOW_SOURCE_OUTPUT_ID,
  emptyLocalSubflowInput,
  newPortId,
  outputBindingsFor,
  replaceAssignedLocalSubflowPort,
} from './localSubflowEditorUtils.ts';

type CallerDrafts = Record<string, {
  input_bindings: LocalSubflowCallStep['input_bindings'];
  output_bindings: LocalSubflowCallStep['output_bindings'];
}>;

const rawStepLabel = (step: Record<string, unknown>) => String(step.name || step.step_type || 'Unnamed step');

const dataInputsFor = (step: Record<string, unknown>) =>
  (Array.isArray(step.data_inputs) ? step.data_inputs : []) as DataInput[];

const assignedPortId = (step: Record<string, unknown>) =>
  assignedLocalSubflowPortId(dataInputsFor(step));

/** Module overview inspector. It exposes lifecycle/interface controls, not root settings. */
const LocalSubflowSidebar = ({ subflow }: { subflow: AutomationLocalSubflow | undefined; stepId?: string }) => {
  const { data: version } = useRetrieveLatestAutomationVersion();
  const { mutate: applyOperations, isPending, validationFeedback } = useCompositionOperations();
  const { target, setActiveCompositionTarget } = useActiveCompositionTarget();
  const moduleIntent = target.scope_kind === 'local_subflow' ? target.intent : undefined;
  const { data: capabilities, isLoading: capabilitiesLoading } = useRetrieveEditorCompositionCapabilities(
    subflow?.subflow_id,
  );
  const [name, setName] = useState(subflow?.name ?? '');
  const [description, setDescription] = useState(subflow?.description ?? '');
  const [inputs, setInputs] = useState<AutomationLocalSubflowPort[]>(subflow?.input_ports ?? []);
  const [outputs, setOutputs] = useState<AutomationLocalSubflowOutputPort[]>(subflow?.output_ports ?? []);
  const [portAssignments, setPortAssignments] = useState<Record<string, string | null>>({});
  const [callerDrafts, setCallerDrafts] = useState<CallerDrafts>({});
  const [deleteOpened, setDeleteOpened] = useState(false);
  const [attachOpened, setAttachOpened] = useState(false);

  const callers = useMemo(
    () => subflow ? callersFor(subflow.subflow_id, version?.steps) : [],
    [subflow, version?.steps],
  );
  const internalSteps = useMemo(() => subflow ? flattenLocalSubflowSteps(subflow.steps) : [], [subflow]);
  const sourceSteps = useMemo(() => internalSteps.flatMap(({ step, siblingIndex }) => {
    const stepId = String(step.step_id ?? '');
    return stepId ? [{ value: stepId, label: rawStepLabel(step) || `Step ${siblingIndex + 1}` }] : [];
  }), [internalSteps]);
  const sourceOutputs = useMemo(() => internalSteps.flatMap(({ step }) => {
    const stepId = String(step.step_id ?? '');
    const outputsForStep = Array.isArray(step.data_outputs) ? step.data_outputs : [];
    const namedOutputs = outputsForStep.flatMap((rawOutput) => {
      const output = rawOutput as Record<string, unknown>;
      const outputId = String(output.output_id ?? '');
      return outputId ? [{ value: `${stepId}:${outputId}`, label: `${rawStepLabel(step)} · ${String(output.name || outputId)}` }] : [];
    });
    if (namedOutputs.length > 0 || typeof step.output_type !== 'string' || !step.output_type) {
      return namedOutputs;
    }
    return [{
      value: `${stepId}:${DEFAULT_LOCAL_SUBFLOW_SOURCE_OUTPUT_ID}`,
      label: `${rawStepLabel(step)} · Default output`,
    }];
  }), [internalSteps]);
  const schemas = useMemo(() => [
    { value: '__none__', label: 'No schema' },
    ...(version?.schemas ?? []).map((schema) => ({
      value: `${schema.schema_id}:${schema.version}`,
      label: schema.description || `${schema.schema_id} v${schema.version}`,
    })),
  ], [version?.schemas]);

  useEffect(() => {
    setName(subflow?.name ?? '');
    setDescription(subflow?.description ?? '');
    setInputs(subflow?.input_ports ?? []);
    setOutputs(subflow?.output_ports ?? []);
    setPortAssignments(Object.fromEntries(internalSteps.map(({ step, siblingIndex }) => [
      String(step.step_id ?? siblingIndex), assignedPortId(step),
    ])));
    setCallerDrafts(Object.fromEntries(callers.map((caller) => [
      caller.step_id,
      { input_bindings: caller.input_bindings, output_bindings: caller.output_bindings },
    ])));
  }, [callers, internalSteps, subflow]);

  const supports = (operation: string) => capabilities?.supported_operations.includes(operation) ?? false;
  const canSaveDetails = supports('rename_local_subflow');
  const canSaveInterface = (
    supports('replace_local_subflow_interface') &&
    supports('replace_data_input_group') &&
    supports('replace_local_subflow_call_bindings')
  );
  const canCreateCall = supports('create_local_subflow_call');
  const canDeleteModule = supports('delete_local_subflow') && supports('delete_local_subflow_call');

  useEffect(() => {
    if (moduleIntent !== 'insert_call' || capabilitiesLoading || !capabilities) return;
    if (canCreateCall && internalSteps.length > 0) {
      setAttachOpened(true);
      return;
    }
    void setActiveCompositionTarget({ scope_kind: 'local_subflow', subflow_id: subflow?.subflow_id });
  }, [
    capabilities,
    capabilitiesLoading,
    canCreateCall,
    internalSteps.length,
    moduleIntent,
    setActiveCompositionTarget,
    subflow?.subflow_id,
  ]);

  if (!subflow) {
    return (
      <Stack p="md">
        <Alert color="yellow" title="Module unavailable">
          This module is no longer in the latest draft.
        </Alert>
        <StyledButton variant="outline" onClick={() => void setActiveCompositionTarget({ scope_kind: 'root' })}>
          Back to workflow
        </StyledButton>
      </Stack>
    );
  }

  const saveInterface = () => applyOperations([
    {
      op_type: 'replace_local_subflow_interface',
      subflow_id: subflow.subflow_id,
      subflow_input_ports: inputs,
      subflow_output_ports: outputs,
    },
    ...Object.entries(portAssignments)
      .filter(([stepId, portId]) => {
        const step = internalSteps.find((candidate) => String(candidate.step.step_id ?? '') === stepId)?.step;
        return step?.step_type === 'transform_data' && assignedPortId(step) !== portId;
      })
      .map<CompositionOperation>(([step_id, port_id]) => ({
        op_type: 'replace_data_input_group',
        subflow_id: subflow.subflow_id,
        step_id,
        field_name: 'data_inputs',
        inputs: replaceAssignedLocalSubflowPort(
          dataInputsFor(
            internalSteps.find((candidate) => String(candidate.step.step_id ?? '') === step_id)?.step ?? {},
          ),
          port_id,
        ),
      })),
    ...callers.map<CompositionOperation>((caller) => ({
      op_type: 'replace_local_subflow_call_bindings',
      step_id: caller.step_id,
      value: {
        input_bindings: inputs.map((port) =>
          callerDrafts[caller.step_id]?.input_bindings.find((binding) => binding.port_id === port.port_id)
          ?? { port_id: port.port_id, data_input: emptyLocalSubflowInput(port.dataset_type) },
        ),
        output_bindings: outputBindingsFor(outputs, callerDrafts[caller.step_id]?.output_bindings ?? []),
      },
    })),
  ]);

  const deleteModule = () => applyOperations(
    [
      ...callers.map<CompositionOperation>((caller) => ({ op_type: 'delete_local_subflow_call', step_id: caller.step_id })),
      { op_type: 'delete_local_subflow', subflow_id: subflow.subflow_id },
    ],
    { onSuccess: () => void setActiveCompositionTarget({ scope_kind: 'root' }) },
  );

  return (
    <Stack p="md" gap="md">
      <Stack gap={2}>
        <Text size="xs" c="dimmed">Workflow / Modules</Text>
        <Group gap="xs">
          <Text fw={700}>Module details</Text>
          <Badge variant="light" color="gray">{internalSteps.length} steps</Badge>
          <Badge variant="light" color="gray">{callers.length} calls</Badge>
        </Group>
        <Text size="sm" c="dimmed">
          Private, typed composition released with this workflow. It has no trigger or standalone runtime.
        </Text>
      </Stack>

      {validationFeedback?.messages.map((message) => <Alert key={message} color="red">{message}</Alert>)}
      {capabilitiesLoading && (
        <Alert color="gray">Loading the editing policy for this module…</Alert>
      )}

      <TextInput
        label="Name"
        value={name}
        disabled={!canSaveDetails}
        onChange={(event) => setName(event.currentTarget.value)}
      />
      <TextInput
        label="Description"
        value={description}
        disabled={!canSaveDetails}
        onChange={(event) => setDescription(event.currentTarget.value)}
      />
      <StyledButton
        style={{ backgroundColor: 'var(--velos-brand-blue)' }}
        loading={isPending}
        disabled={!canSaveDetails || !name.trim()}
        onClick={() => applyOperations([{
          op_type: 'rename_local_subflow',
          subflow_id: subflow.subflow_id,
          subflow_name: name.trim(),
          subflow_description: description,
        }])}
      >
        Save details
      </StyledButton>

      <Divider label="Typed interface" labelPosition="left" />
      <Text size="xs" c="dimmed">
        Saving validates the complete interface, internal input-port assignments, and every affected root caller together.
      </Text>
      {canSaveInterface ? (
        <>
          <PortList
            kind="input"
            ports={inputs}
            schemas={schemas}
            onChange={setInputs}
            onRemove={(index) => {
              const removed = inputs[index];
              setInputs((current) => current.filter((_, itemIndex) => itemIndex !== index));
              setPortAssignments((current) => Object.fromEntries(
                Object.entries(current).map(([stepId, portId]) => [stepId, portId === removed?.port_id ? null : portId]),
              ));
            }}
            onAdd={() => setInputs((current) => [...current, {
              port_id: newPortId(), name: 'New input', dataset_type: DatasetType.Json, schema_ref: null, required: true,
            }])}
          />
          <PortList
            kind="output"
            ports={outputs}
            schemas={schemas}
            sourceSteps={sourceSteps}
            sourceOutputs={sourceOutputs}
            onChange={setOutputs}
            onRemove={(index) => setOutputs((current) => current.filter((_, itemIndex) => itemIndex !== index))}
            onAdd={() => {
              const sourceStepId = sourceSteps[0]?.value;
              if (!sourceStepId) return;
              setOutputs((current) => [...current, {
                port_id: newPortId(),
                name: 'New output',
                dataset_type: DatasetType.Json,
                schema_ref: null,
                required: true,
                source_step_id: sourceStepId,
                source_output_id: null,
              }]);
            }}
          />
          {internalSteps
            .filter(({ step }) => step.step_type === 'transform_data')
            .map(({ step, siblingIndex }) => {
              const stepId = String(step.step_id ?? siblingIndex);
              return (
                <StyledSelect
                  key={stepId}
                  label={`${rawStepLabel(step)} module input`}
                  placeholder="No module input"
                  data={inputs.map((port) => ({ value: port.port_id, label: port.name || port.port_id }))}
                  value={portAssignments[stepId] ?? null}
                  onChange={(value) => setPortAssignments((current) => ({ ...current, [stepId]: value }))}
                  clearable
                />
              );
            })}
        </>
      ) : (
        <Alert color="gray">
          {capabilities?.read_only_reason ?? 'The module interface is not available for editing.'}
        </Alert>
      )}

      <Divider label={`Callers (${callers.length})`} labelPosition="left" />
      {callers.length === 0 ? (
        <Text size="sm" c="dimmed">No root workflow calls use this module yet.</Text>
      ) : callers.map((caller) => (
        <Paper key={caller.step_id} withBorder p="sm">
          <Group justify="space-between" mb="xs">
            <Stack gap={0}>
              <Text fw={500}>{caller.name || caller.step_id}</Text>
              <Text size="xs" c="dimmed">{caller.input_bindings.length} inputs · {caller.output_bindings.length} outputs</Text>
            </Stack>
            <StyledButton
              size="sm"
              variant="subtle"
              onClick={() => void setActiveCompositionTarget({ scope_kind: 'root', step_id: caller.step_id })}
            >
              Open caller
            </StyledButton>
          </Group>
          {inputs.map((port) => {
            const binding = callerDrafts[caller.step_id]?.input_bindings.find((item) => item.port_id === port.port_id);
            return (
              <SingleDataInputSelectorInner
                key={port.port_id}
                step={caller}
                label={port.name || port.port_id}
                dataInput={binding?.data_input ?? emptyLocalSubflowInput(port.dataset_type)}
                includeEmptyDatasets
                scopeOverride="root"
                onChange={(dataInput) => setCallerDrafts((drafts) => ({
                  ...drafts,
                  [caller.step_id]: {
                    input_bindings: inputs.map((candidate) =>
                      candidate.port_id === port.port_id
                        ? { port_id: candidate.port_id, data_input: dataInput }
                        : drafts[caller.step_id]?.input_bindings.find((item) => item.port_id === candidate.port_id)
                          ?? { port_id: candidate.port_id, data_input: emptyLocalSubflowInput(candidate.dataset_type) },
                    ),
                    output_bindings: drafts[caller.step_id]?.output_bindings ?? caller.output_bindings,
                  },
                }))}
              />
            );
          })}
        </Paper>
      ))}
      <StyledButton
        style={{ backgroundColor: 'var(--velos-brand-blue)' }}
        loading={isPending}
        disabled={!canSaveInterface}
        onClick={saveInterface}
      >
        Validate and save interface
      </StyledButton>

      <Divider label="Workflow insertion" labelPosition="left" />
      <StyledButton
        variant="outline"
        disabled={!canCreateCall || internalSteps.length === 0}
        onClick={() => setAttachOpened(true)}
      >
        Insert into workflow
      </StyledButton>
      {internalSteps.length === 0 && (
        <Text size="xs" c="dimmed">Add an internal step before inserting a root call.</Text>
      )}
      <AttachCall
        opened={attachOpened}
        subflow={subflow}
        rootSteps={(version?.steps ?? []) as AutomationStep[]}
        isPending={isPending}
        onClose={() => {
          setAttachOpened(false);
          if (target.scope_kind === 'local_subflow' && target.intent) {
            void setActiveCompositionTarget({ scope_kind: 'local_subflow', subflow_id: subflow.subflow_id });
          }
        }}
        onApply={applyOperations}
      />

      <Divider />
      <StyledButton
        color="red"
        variant="outline"
        disabled={!canDeleteModule}
        onClick={() => setDeleteOpened(true)}
      >
        Delete module
      </StyledButton>
      <ConfirmationModal
        opened={deleteOpened}
        onClose={() => setDeleteOpened(false)}
        title="Delete module and calls?"
        message={`This removes ${callers.length} root call${callers.length === 1 ? '' : 's'} and this private module from the draft.`}
        confirmText="Delete module"
        confirmType="delete"
        confirmLoading={isPending}
        onConfirm={deleteModule}
      />
    </Stack>
  );
};

export default LocalSubflowSidebar;
